import { pluralize } from '@art-suite/art-core-ts';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  countScopeNavigatorFilterTotals,
  countTasksByScopeAttentionFilter,
  DEFAULT_NEEDS_ATTENTION_TITLE,
  type ScopeAttentionFilterType,
  toModifierFilterKey,
  toModifierFilterKeys,
} from '@/Client/Lib/scopeAttentionFilterLib';
import { isScopeNavigatorTask } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { type ScopeAttentionFilterContextValue, type TaskModifier } from '@/Client/State';
import { NeedsAttentionIndicator } from '@/Client/Widgets';
import type { OverallTaskStatus, TaskStatusPhase } from '@kaiizen/kai-pro-api';
import type { ScanGroupOptimized } from '@kaiizen/kai-scan-template-system';
import { FilterButton } from './FilterButton';
import { ScopeFilterGroups } from './ScopeFilterGroups';
import { ScopeFilterSection } from './ScopeFilterSection';
import { DEFAULT_SCOPE_FILTER_STYLES, ScopeFilterStyles } from './scopeFilterStyles';
import { ScopeFilterTaskGroups, TASK_STATUS_VALUES, type TaskStatusCounts } from './ScopeFilterTaskGroups';

type FilterGroupId = 'flags' | 'modifiers';

type FilterDef = {
  key: ScopeAttentionFilterType;
  label: string;
  group: FilterGroupId;
};

/**
 * Order within the popover. The modifiers group is absent here on purpose: it is whatever the org
 * has configured, resolved at render (KAIDEV-4206), rather than a pair this file knows about.
 */
export const FILTER_DEFS: readonly FilterDef[] = [
  { key: 'missingProduct', label: 'Missing product', group: 'flags' },
  { key: 'unassignedContractors', label: 'Unassigned contractor', group: 'flags' },
  { key: 'outOfStock', label: 'Out of stock', group: 'flags' },
  { key: 'changeOrders', label: 'Change order', group: 'flags' },
];

/**
 * The menu belongs to the sidebar rather than the trigger, so its left edge
 * follows the sidebar's left edge with the same small inset used at the
 * viewport boundary. It is portalled to the body to avoid the scroll
 * container's clipping, but remains within the viewport when possible.
 */
const getMenuPosition = (anchorRect: DOMRect, menuWidth: number, boundsLeft: number) => {
  const viewportPadding = 8;
  const sidebarLeft = Math.max(viewportPadding, boundsLeft + viewportPadding);
  const maxLeft = Math.max(viewportPadding, window.innerWidth - menuWidth - viewportPadding);
  const top = anchorRect.bottom + 6;
  return {
    top,
    left: Math.min(sidebarLeft, maxLeft),
    maxHeight: Math.max(0, window.innerHeight - top - viewportPadding),
  };
};

export const ScopeFilterDropdown = ({
  groups,
  filters,
  overallTaskStatusByTaskId,
  styles = DEFAULT_SCOPE_FILTER_STYLES,
  taskStatusPhase,
  knownTaskModifiers = [],
}: {
  groups: readonly ScanGroupOptimized[];
  filters: ScopeAttentionFilterContextValue;
  overallTaskStatusByTaskId: Readonly<Record<string, OverallTaskStatus>>;
  /**
   * The org's modifiers, which become the Modifiers group's rows. Disabled ones included: their
   * tags are still on live tasks, so dropping them here would make that work unfindable.
   */
  knownTaskModifiers?: readonly TaskModifier[];
  styles?: ScopeFilterStyles;
  taskStatusPhase: TaskStatusPhase;
}) => {
  const { activeFilters, activeTaskStatus, evalContext, toggleFilter, toggleTaskStatus, clearFilters } = filters;
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, maxHeight: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const modifierFilterKeys = useMemo(() => toModifierFilterKeys(knownTaskModifiers), [knownTaskModifiers]);

  const counts = useMemo(
    () => countTasksByScopeAttentionFilter(groups, evalContext, modifierFilterKeys),
    [groups, evalContext, modifierFilterKeys],
  );

  // One row per modifier the org has, named as the org named it.
  const modifierFilterRows = useMemo(
    () =>
      knownTaskModifiers.map(modifier => {
        const key = toModifierFilterKey(modifier.lineItemTag);
        return {
          key,
          label: modifier.displayName,
          count: counts[key] ?? 0,
          selected: activeFilters.has(key),
          onToggle: () => toggleFilter(key),
        };
      }),
    [activeFilters, counts, knownTaskModifiers, toggleFilter],
  );
  const { filteredCount, totalCount } = useMemo(
    () => countScopeNavigatorFilterTotals(groups, activeFilters, evalContext),
    [groups, activeFilters, evalContext],
  );

  const activeCount = activeFilters.size + (activeTaskStatus ? 1 : 0);

  const taskStatusCounts = useMemo(() => {
    const counts = Object.fromEntries(TASK_STATUS_VALUES.map(status => [status, 0])) as TaskStatusCounts;
    groups.forEach(group => {
      (group.tasks ?? []).filter(isScopeNavigatorTask).forEach(task => {
        const status = overallTaskStatusByTaskId[task.id];
        counts[status ?? 'draft'] += 1;
      });
    });
    return counts;
  }, [groups, overallTaskStatusByTaskId]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const updateMenuPosition = () => {
      const anchor = buttonRef.current;
      const anchorRect = anchor?.getBoundingClientRect();
      if (!anchor || !anchorRect) return;
      const menuWidth = menuRef.current?.offsetWidth || styles.menuMinWidth;
      const boundsLeft = anchor.closest('aside')?.getBoundingClientRect().left ?? 0;
      setMenuPosition(getMenuPosition(anchorRect, menuWidth, boundsLeft));
    };
    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open, activeCount, styles.menuMinWidth]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div css={styles.container}>
      <FilterButton
        open={open}
        buttonRef={buttonRef}
        filterIsActive={activeCount > 0}
        onClick={() => setOpen(previous => !previous)}
      />

      {open
        ? createPortal(
          <div
            ref={menuRef}
            css={styles.menu}
            role={'group'}
            aria-label={'Filters'}
            data-testid={'scope-attention-filter-menu'}
            style={{ top: menuPosition.top, left: menuPosition.left, maxHeight: menuPosition.maxHeight }}
          >
            <div css={styles.menuHeader} data-testid={'scope-attention-filter-header'}>{'Filters'}</div>
            <div css={styles.menuContent} data-testid={'scope-attention-filter-content'}>
              <ScopeFilterGroups
                groups={[
                  {
                    id: 'flags',
                    label: 'Flags',
                    content: (styles: ScopeFilterStyles) => (
                      <ScopeFilterSection
                        testId={'scope-attention-filter-rows-flags'}
                        styles={styles}
                        rows={FILTER_DEFS.filter(({ group }) => group === 'flags').map(({ key, label }) => ({
                          key,
                          label,
                          indicator: (
                            <NeedsAttentionIndicator
                              active={counts[key] > 0}
                              size={13}
                              label={DEFAULT_NEEDS_ATTENTION_TITLE}
                            />
                          ),
                          count: counts[key],
                          selected: activeFilters.has(key),
                          onToggle: () => toggleFilter(key),
                        }))}
                      />
                    ),
                  },
                  {
                    id: 'taskStatus',
                    label: 'Task Status',
                    content: (styles: ScopeFilterStyles) => (
                      <ScopeFilterTaskGroups
                        phase={taskStatusPhase}
                        counts={taskStatusCounts}
                        selectedStatus={activeTaskStatus}
                        onStatusToggle={toggleTaskStatus}
                        styles={styles}
                      />
                    ),
                  },
                  {
                    id: 'modifiers',
                    label: 'Modifiers',
                    content: (styles: ScopeFilterStyles) => (
                      <ScopeFilterSection
                        testId={'scope-attention-filter-rows-modifiers'}
                        styles={{ ...styles, rows: styles.compactRows }}
                        rows={modifierFilterRows}
                      />
                    ),
                  },
                ]}
              />
            </div>

            <div css={styles.footer} data-testid={'scope-attention-filter-footer'}>
              <span css={styles.taskDetails} data-testid={'scope-attention-filter-footer-task-details'}>
                {`Showing ${filteredCount} of ${pluralize(totalCount, 'task')} total`}
              </span>
              <button type={'button'} css={styles.resetButton} onClick={clearFilters} disabled={activeCount === 0}>
                {'Clear'}
              </button>
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
};
